import React, { CSSProperties } from 'react';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { MapAddressIcon, MapDeviceImeiIcon, MapDistanceIcon, MapSearch01Icon, MapUpdateTimeIcon } from '~/assets/icons';
import type { IPersonStatus } from '~/utils/interface/map';
import styles from '../Map.module.scss';
import type { TrackingDevice } from '../mapTrackingTypes';
import { formatTrackingStatusDuration } from '../formatStatusDuration';
import { getStatusActiveBackgroundColor, getStatusStyleVars } from '../statusPerson';

const cx = classNames.bind(styles);

interface InfoRowProps {
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  text: string;
}

const InfoRow: React.FC<InfoRowProps> = ({ Icon, text }) => (
  <div className='flex items-center gap-2'>
    <Icon className='w-4 h-4 flex-shrink-0 opacity-60' />
    <span className='font-["Manrope",sans-serif] text-[13px] font-normal leading-[1.4] tracking-normal text-[#161A1F] flex-1 min-w-0 truncate line-clamp-1'>
      {text}
    </span>
  </div>
);

interface DeviceCardProps {
  device: TrackingDevice;
  statusDurationLabel: (minutes: number) => string;
  isHighlighted?: boolean;
  isSelected?: boolean;
  onHoverChange?: (userId: number | null) => void;
  onSelect?: (userId: number) => void;
  personStatuses: IPersonStatus[];
}

const DeviceCard: React.FC<DeviceCardProps> = React.memo(({
  device,
  statusDurationLabel,
  isHighlighted = false,
  isSelected = false,
  onHoverChange,
  onSelect,
  personStatuses,
}) => {
  const isActive = isSelected || isHighlighted;
  const activeBackgroundColor = getStatusActiveBackgroundColor(device.status, personStatuses);
  const statusStyleVars = getStatusStyleVars(device.status, personStatuses);

  return (
    <div
      className={cx(
        'tracking-card',
        'border rounded-xl mb-2 last:mb-0 transition-all duration-150 cursor-pointer overflow-hidden flex flex-col gap-2',
        {
          'tracking-card-active': isActive,
          'tracking-card-selected': isSelected,
          'tracking-card-highlighted': isHighlighted && !isSelected,
          'shadow-sm': isActive,
        }
      )}
      style={{ ...statusStyleVars, backgroundColor: isActive ? activeBackgroundColor : undefined } as CSSProperties}
      onMouseEnter={() => onHoverChange?.(device.person_id)}
      onMouseLeave={() => onHoverChange?.(null)}
      onClick={() => onSelect?.(device.person_id)}
    >
      <div className='flex items-center justify-between gap-2 px-3 pt-3'>
        <span className={cx('tracking-card-name', 'font-semibold text-sm flex-1 min-w-0 truncate')}>
          {device.full_name}
        </span>
        <span className={cx('tracking-card-duration', 'text-xs flex-shrink-0 ml-1')}>
          {Number.isFinite(device.status_duration) ? statusDurationLabel(device.status_duration) : '--'}
        </span>
      </div>

      <div className={cx('tracking-card-divider')} />

      <div className='flex flex-col gap-1.5 px-3 pb-3'>
        <InfoRow Icon={MapAddressIcon} text={device.address || '--'} />
        <InfoRow Icon={MapUpdateTimeIcon} text={device.updatetime || '--'} />
        <InfoRow
          Icon={MapDistanceIcon}
          text={device.today_distance !== '--' ? `${Math.round(Number(device.today_distance))} km` : '--'}
        />
        <InfoRow Icon={MapDeviceImeiIcon} text={device.device_imei || '--'} />
      </div>
    </div>
  );
});

DeviceCard.displayName = 'DeviceCard';

interface MapTrackingListProps {
  devices: TrackingDevice[];
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  isLoading?: boolean;
  hoveredUserId: number | null;
  onHoveredUserChange: (userId: number | null) => void;
  selectedUserId: number | null;
  onSelectUser: (userId: number | null) => void;
  personStatuses: IPersonStatus[];
}

const MapTrackingList: React.FC<MapTrackingListProps> = ({
  devices,
  searchQuery,
  onSearchQueryChange,
  isLoading = false,
  hoveredUserId,
  onHoveredUserChange,
  selectedUserId,
  onSelectUser,
  personStatuses,
}) => {
  const { t } = useTranslation('admin/map');

  return (
    <>
      <div
        className={cx(
          'MapTrackingList',
          'flex flex-col h-full min-h-0 shrink-0 bg-white border border-[#DFDFDF] shadow-sm rounded-xl overflow-hidden'
        )}
        style={{ width: 'clamp(240px, 25vw, 340px)', flexBasis: 'clamp(240px, 25vw, 340px)' }}
      >
        <div className='flex-shrink-0 px-4 pt-4 pb-3 border-b border-gray-100'>
          <h2 className={cx('list-title', 'mb-3 text-black')}>{t('trackingList.title')}</h2>

          <div className='relative h-[52px] rounded-xl border-2 border-[#D7DEE7] bg-white p-[14px]'>
            <MapSearch01Icon className='absolute left-[14px] top-1/2 -translate-y-1/2 w-4 h-4 opacity-50 pointer-events-none' />
            <input
              type='text'
              placeholder={t('trackingList.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => onSearchQueryChange(e.target.value)}
              className='h-full w-full border-0 bg-transparent pl-8 pr-0 text-sm text-[#0F172A] placeholder:text-gray-400 outline-none'
            />
          </div>
        </div>

        <div className={cx('custom-scrollbar', 'flex-1 overflow-y-auto px-4 py-3')}>
          {isLoading && devices.length === 0 ? (
            <p className='text-sm text-gray-400 text-center mt-10'>{t('trackingList.loading')}</p>
          ) : devices.length > 0 ? (
            devices.map((device) => (
              <DeviceCard
                key={device.person_id}
                device={device}
                statusDurationLabel={(minutes) => formatTrackingStatusDuration(minutes, t('trackingList.ago'))}
                isHighlighted={hoveredUserId === device.person_id}
                isSelected={selectedUserId === device.person_id}
                onHoverChange={onHoveredUserChange}
                onSelect={(id) => onSelectUser(id)}
                personStatuses={personStatuses}
              />
            ))
          ) : (
            <p className='text-sm text-gray-400 text-center mt-10'>{t('trackingList.empty')}</p>
          )}
        </div>
      </div>
    </>
  );
};

export default MapTrackingList;
